import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Prop } from "@eastsideco/salvo-ts";
import { ProductsCarousel } from "../ProductsCarousel/ProductsCarousel";
import { VoyadoProductCard } from "./VoyadoProductCard";
import { ProductDetails } from "../Product/ProductDetails";
import {
    elevate,
    type Algorithm,
    type RecommendationList,
    type CartPage,
    type ProductPage,
    type LandingPage,
} from '@apptus/esales-api';
import { cart } from '@/theme/Cart';
import { resetElevateSessionIfTrackingConsentDenied } from "@/utils/elevate";

declare global {
    interface Window {
        elevate: ReturnType<typeof elevate>;
    }
}

@Component
export class VoyadoRecommendations extends ThemeComponent {
    @Prop({ default: 'landing' }) page!: 'landing' | 'product' | 'cart';
    @Prop({ default: '' }) pageReference!: string;
    @Prop({ default: 'TOP_PRODUCTS' }) algorithm!: string;
    @Prop({ default: '' }) listId!: string;

    private _recommendations?: RecommendationList;

    set recommendations(recommendations: RecommendationList | undefined) {
        this._recommendations = recommendations;
        this.renderRecommendations();
    }

    get recommendations() {
        return this._recommendations;
    }

    init() {
        const interval = setInterval(() => {
            if (!window.elevate) {
                return;
            }

            switch(this.page) {
                case 'landing':
                    void this.fetchLandingPageRecommendations();
                    break;
                case 'product':
                    void this.listenForProductLoad();
                    break;
                case 'cart':
                    void this.fetchCartRecommendations();

                    document.addEventListener('cartChanged', () => {
                        void this.fetchCartRecommendations();
                    });

                    break;
            }

            clearInterval(interval);
        }, 200);

    }

    async fetchLandingPageRecommendations() {
        if (! window.elevate) {
            return;
        }

        resetElevateSessionIfTrackingConsentDenied();

        const result = await window.elevate.query.landingPage(
            { pageReference: this.pageReference || window.location.pathname },
            {
                primaryList: {
                    include: false,
                },
                recommendationLists: [this.recommendationListConfig()]
            }
        );

        this.parseResult(result);
    }

    async fetchCartRecommendations() {
        if (! window.elevate) {
            return;
        }

        resetElevateSessionIfTrackingConsentDenied();

        const result = await window.elevate.query.cartPage(
            { cart: cart.items.map((item) => item.productId.toString()) },
            { recommendationLists: [this.recommendationListConfig()] }
        );

        this.parseResult(result);
    }

    listenForProductLoad() {
        const productDetails = document.querySelector('product-details') as ProductDetails;

        this.$subscribe(productDetails.onVoyadoProductLoaded, (result) => {
            this.parseResult(result);
        });
    }

    private recommendationListConfig() {
        return {
            id: this.listId || this.page,
            algorithm: this.algorithm as Algorithm,
        };
    }

    parseResult(result: CartPage | ProductPage | LandingPage) {
        if (result.recommendationLists.length > 0) {
            this.recommendations = result.recommendationLists.find(({ algorithm }) => algorithm == this.algorithm);
        } else {
            this.recommendations = undefined;
        }

        this.closest('.featured-products__wrapper')?.classList.toggle(
            'featured-products__wrapper--hidden',
            this.recommendations === undefined
        );
    }

    renderRecommendations() {
        if (!this.recommendations) {
            return;
        }

        const carousel = new ProductsCarousel;
        carousel.classList.add('products-carousel');

        const carouselInner = document.createElement('div');
        carouselInner.classList.add('products-carousel__inner');
        carouselInner.setAttribute('data-ref', 'container');

        const documentFragment = document.createDocumentFragment();

        for (const productGroup of this.recommendations.productGroups) {
            const card = new VoyadoProductCard;
            card.classList.add('product-card--carousel', 'products-carousel__item');
            card.setAttribute('data-ref', 'carouselCard');
            card.productGroup = productGroup;

            documentFragment.appendChild(card);
        }

        carouselInner.replaceChildren(documentFragment);
        carousel.replaceChildren(carouselInner);

        this.replaceChildren(carousel);
    }
}
